// @ts-nocheck
import { TAU, clamp, lerp, rand, rgba } from "../../util";
import { Sound } from "../../audio";
import { Particle } from "../../entities";
import { uiFont } from "../../ui-tokens";
import { Game } from "../game-base";

Game.METEOR_NUKE_CRATER_LIFE = 4;
Game.METEOR_NUKE_SHOCK_DUR = 1.05;
Game.METEOR_NUKE_VOID_CARRY = 520;
Game.METEOR_NUKE_SWAMP_LIFE = 3.4;
Game.METEOR_NUKE_FUSE_DUR = 5.0;
Game.JUDGMENT_FUSE_DUR = 1.5;
Game.INFERNO_NOVA_WINDUP = 3.0;
Game.PLAGUE_BURST_WINDUP = 2.8;
Game.METEOR_NUKE_RING_WIDTH = 64;
const NUKE_CLOUD_SECTORS = 96;
/** Sector is blocked only when a ray actually hits terrain well before max range. */
const NUKE_REACH_BLOCKED_SLACK = 12;
const NUKE_MIN_BLOCK_REACH = 40;

function pointInOBB(ox, oy, cx, cy, dx, dy, halfLen, halfThick) {
  const tx = -dy, ty = dx;
  const lx = (ox - cx) * dx + (oy - cy) * dy;
  const ly = (ox - cx) * tx + (oy - cy) * ty;
  return Math.abs(lx) <= halfLen && Math.abs(ly) <= halfThick;
}

function rayCircleHitDist(ox, oy, ux, uy, maxDist, cx, cy, r) {
  const fx = cx - ox, fy = cy - oy;
  const t = fx * ux + fy * uy;
  const perp = fx * (-uy) + fy * ux;
  if (Math.abs(perp) > r) return null;
  const inside = r * r - perp * perp;
  if (inside < 0) return null;
  const distSq = fx * fx + fy * fy;
  if (distSq <= r * r * (1 + 1e-6)) {
    const exit = t + Math.sqrt(inside);
    if (exit <= 0 || exit > maxDist) return null;
    return exit;
  }
  const hit = t - Math.sqrt(inside);
  if (hit < NUKE_MIN_BLOCK_REACH || hit > maxDist) return null;
  return hit;
}

function rayOBBHitDist(ox, oy, ux, uy, maxDist, cx, cy, dx, dy, halfLen, halfThick) {
  const tx = -dy, ty = dx;
  const oLx = (ox - cx) * dx + (oy - cy) * dy;
  const oLy = (ox - cx) * tx + (oy - cy) * ty;
  const dLx = ux * dx + uy * dy;
  const dLy = ux * tx + uy * ty;
  let tMin = 0, tMax = maxDist;
  let clipped = false;
  for (const slab of [[oLx, dLx, -halfLen, halfLen], [oLy, dLy, -halfThick, halfThick]]) {
    const o = slab[0], d = slab[1], min = slab[2], max = slab[3];
    if (Math.abs(d) < 1e-9) {
      if (o < min || o > max) return null;
      continue;
    }
    clipped = true;
    let t1 = (min - o) / d, t2 = (max - o) / d;
    if (t1 > t2) { const tmp = t1; t1 = t2; t2 = tmp; }
    if (t1 > tMin) tMin = t1;
    if (t2 < tMax) tMax = t2;
    if (tMin > tMax) return null;
  }
  const inside = pointInOBB(ox, oy, cx, cy, dx, dy, halfLen, halfThick);
  if (!clipped && !inside) return null;
  if (tMax < 0) return inside ? 0 : null;
  if (tMin < 0) {
    if (!inside) return null;
    tMin = 0;
  }
  if (tMin > maxDist) return null;
  if (!inside && tMin < NUKE_MIN_BLOCK_REACH) return null;
  return tMin;
}

function fillAnnulusArc(c, a0, a1, rInner, rOuter) {
  if (rOuter <= rInner + 2) return;
  c.beginPath();
  c.arc(0, 0, rOuter, a0, a1);
  c.arc(0, 0, Math.max(0, rInner), a1, a0, true);
  c.closePath();
}

/** Contiguous runs on a circular flag array (e.g. open vs blocked sectors). */
function circularRuns(flags, fn) {
  const n = flags.length;
  if (!n) return;
  let uniform = true;
  for (let i = 1; i < n; i++) {
    if (flags[i] !== flags[0]) { uniform = false; break; }
  }
  if (uniform) {
    fn(0, n, flags[0]);
    return;
  }
  let start = 0;
  while (start < n && flags[start] === flags[n - 1]) start++;
  start %= n;
  let i = start;
  do {
    const v = flags[i];
    let j = i + 1;
    while (j < start + n && flags[j % n] === v) j++;
    fn(i % n, j - i, v);
    i = j % n;
  } while (i !== start);
}

/** Angular umbra behind a circular obstacle — visual only, not gameplay reach. */
function capCircleShadow(reach, ox, oy, cx, cy, r, n) {
  const dx = cx - ox, dy = cy - oy;
  const d = Math.hypot(dx, dy);
  if (d < 1e-6) return;
  const near = d <= r ? 0 : d - r;
  if (near < NUKE_MIN_BLOCK_REACH) return;
  const halfAng = d <= r ? Math.PI : Math.asin(Math.min(1, r / d));
  const base = Math.atan2(dy, dx);
  const sw = TAU / n;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU;
    const da = Math.atan2(Math.sin(a - base), Math.cos(a - base));
    if (Math.abs(da) <= halfAng + sw * 0.85) reach[i] = Math.min(reach[i], near);
  }
}

function capOBBShadow(reach, ox, oy, cx, cy, dx, dy, halfLen, halfThick, n) {
  const tx = -dy, ty = dx;
  const corners = [
    [-halfLen, -halfThick], [halfLen, -halfThick], [halfLen, halfThick], [-halfLen, halfThick],
  ];
  for (const [lx, ly] of corners) {
    const px = cx + lx * dx + ly * tx;
    const py = cy + lx * dy + ly * ty;
    capCircleShadow(reach, ox, oy, px, py, 12, n);
  }
  capCircleShadow(reach, ox, oy, cx, cy, Math.min(halfLen, halfThick) * 0.55, n);
}

Game.prototype.computeMeteorNukeReach = function (ox, oy, maxR) {
  const n = NUKE_CLOUD_SECTORS;
  const raw = new Float32Array(n);
  raw.fill(maxR);
  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU;
    const ux = Math.cos(a), uy = Math.sin(a);
    for (const pl of this.pillars || []) {
      if (!pl.active) continue;
      const hit = rayCircleHitDist(ox, oy, ux, uy, maxR, pl.x, pl.y, pl.r);
      if (hit != null && hit < raw[i]) raw[i] = hit;
    }
    for (const iw of this.icewalls || []) {
      const hx = iw.hx || 68, hy = iw.hy || 38;
      const hit = rayOBBHitDist(ox, oy, ux, uy, maxR, iw.x, iw.y, iw.dx, iw.dy, hx, hy);
      if (hit != null && hit < raw[i]) raw[i] = hit;
    }
  }
  return raw;
};

Game.prototype.computeMeteorNukeShadowReach = function (ox, oy, maxR, rayReach) {
  const reach = rayReach ? new Float32Array(rayReach) : this.computeMeteorNukeReach(ox, oy, maxR);
  const n = reach.length;
  for (const pl of this.pillars || []) {
    if (!pl.active) continue;
    capCircleShadow(reach, ox, oy, pl.x, pl.y, pl.r, n);
  }
  for (const iw of this.icewalls || []) {
    capOBBShadow(reach, ox, oy, iw.x, iw.y, iw.dx, iw.dy, iw.hx || 68, iw.hy || 38, n);
  }
  return reach;
};

Game.prototype.meteorNukeShadowSectorBlocked = function (cloud, sector) {
  const reach = cloud.shadowReach[sector];
  return reach < cloud.maxR - NUKE_REACH_BLOCKED_SLACK && reach >= NUKE_MIN_BLOCK_REACH;
};

Game.prototype.meteorNukeSectorInUmbra = function (cloud, sector) {
  return this.meteorNukeShadowSectorBlocked(cloud, sector)
    || this.meteorNukeSectorBlocked(cloud, sector);
};

Game.prototype.meteorNukeUmbraCap = function (cloud, sector) {
  let cap = cloud.maxR;
  if (this.meteorNukeShadowSectorBlocked(cloud, sector)) {
    cap = Math.min(cap, cloud.shadowReach[sector]);
  }
  if (this.meteorNukeSectorBlocked(cloud, sector)) {
    cap = Math.min(cap, cloud.reach[sector]);
  }
  return cap;
};

Game.prototype.spawnMeteorCrater = function (x, y, r) {
  this.spawnFloorBurn("nuke", x, y, r);
};

/** First ground impact — local blast, mushroom cloud, crater. */
Game.prototype.spawnMeteorNukeImpact = function (x, y, blastR) {
  const r = blastR || 200;
  Sound.boom();
  this.fireBurst(x, y, 44, 1.8);
  for (let i = 0; i < 36; i++) {
    const a = rand(0, TAU);
    const sp = rand(40, 160);
    this.particles.push(new Particle(
      x + Math.cos(a) * rand(0, r * 0.15), y + Math.sin(a) * rand(0, r * 0.1),
      Math.cos(a) * sp * 0.35, -rand(90, 220),
      rand(0.55, 1.1), Math.random() < 0.55 ? "#ff7a2f" : "#ffd479",
      rand(6, 14), { kind: "glow", grav: -35 }));
  }
  for (let i = 0; i < 28; i++) {
    const a = rand(0, TAU);
    this.particles.push(new Particle(
      x + Math.cos(a) * rand(0, r * 0.25), y + Math.sin(a) * rand(0, r * 0.2),
      Math.cos(a) * rand(20, 70), -rand(30, 90),
      rand(0.8, 1.6), Math.random() < 0.5 ? "#3a2418" : "#5a3828",
      rand(10, 22), { kind: "smoke", grav: -18, drag: 0.92 }));
  }
  for (let i = 0; i < 16; i++) {
    const a = rand(0, TAU);
    const dist = rand(r * 0.35, r * 0.85);
    this.particles.push(new Particle(
      x + Math.cos(a) * dist, y + Math.sin(a) * dist * 0.55 - r * 0.2,
      rand(-25, 25), rand(-40, 20),
      rand(0.9, 1.5), "#2a1810",
      rand(14, 28), { kind: "smoke", grav: -8, drag: 0.95 }));
  }
  this.spawnShockwave(x, y, r, "#ffb060", { life: 0.35, width: 8 });
  this.spawnShockwave(x, y, r * 0.72, "#ff6a3a", { life: 0.42, width: 5 });
  this.spawnMeteorCrater(x, y, r * 0.72);
  this.spawnMeteorNukeFuse(x, y);
};

Game.prototype.meteorNukeShockwaveMaxR = function (x, y) {
  const d = Math.hypot(x, y);
  const worldR = this.maxRadius || 950;
  return d + worldR + Game.METEOR_NUKE_VOID_CARRY;
};

/** Delayed shockwave — expanding cloud ring, blocked and vaporized by terrain. */
Game.prototype.spawnMeteorNukeShockwave = function (x, y, maxR, opts) {
  opts = opts || {};
  const r = maxR || this.meteorNukeShockwaveMaxR(x, y);
  const dur = opts.dur ?? Game.METEOR_NUKE_SHOCK_DUR;
  if (!this.meteorNukeClouds) this.meteorNukeClouds = [];
  if (opts.clearFuse !== false) this.clearMeteorNukeFuse(x, y);
  const reach = this.computeMeteorNukeReach(x, y, r);
  this.meteorNukeClouds.push({
    x, y, maxR: r, life: dur, t: 0,
    slowHalf: !!this._bulletTimeActive,
    reach,
    shadowReach: this.computeMeteorNukeShadowReach(x, y, r, reach),
    blocked: new Uint8Array(reach.length),
    blockedAge: new Float32Array(reach.length),
    seed: Math.random() * 1000,
    prevR: 0,
    partAcc: 0,
  });
  if (opts.burst !== false) {
    this.fireBurst(x, y, opts.burstCount ?? 20, opts.burstScale ?? 1.0);
  }
  if (opts.swamp) this.spawnMeteorNukeSwampCloud(x, y);
};

Game.prototype.spawnMeteorNukeSwampCloud = function (x, y) {
  if (!this.meteorNukeSwamps) this.meteorNukeSwamps = [];
  this.meteorNukeSwamps.push({
    x, y, t: 0, life: Game.METEOR_NUKE_SWAMP_LIFE,
    slowHalf: !!this._bulletTimeActive,
    seed: Math.random() * 1000,
    puffAcc: 0,
  });
  for (let i = 0; i < 22; i++) {
    const a = rand(0, TAU);
    const sp = rand(60, 200);
    this.particles.push(new Particle(
      x + Math.cos(a) * rand(0, 18), y + Math.sin(a) * rand(0, 14),
      Math.cos(a) * sp * 0.25, -rand(120, 280),
      rand(0.35, 0.75), Math.random() < 0.45 ? "#6a9050" : "#4a6838",
      rand(8, 18), { kind: "smoke", grav: -28, drag: 0.9 }));
  }
  for (let i = 0; i < 14; i++) {
    this.particles.push(new Particle(
      x + rand(-12, 12), y + rand(-10, 10),
      rand(-40, 40), -rand(160, 320),
      rand(0.25, 0.55), Math.random() < 0.5 ? "#c87840" : "#8a5030",
      rand(5, 11), { kind: "glow", grav: -42, drag: 0.88 }));
  }
};

Game.prototype.updateMeteorNukeSwamps = function (dt) {
  if (!this.meteorNukeSwamps?.length) return;
  for (const sw of this.meteorNukeSwamps) {
    const bdt = sw.slowHalf ? dt * Game.BULLET_TIME_FX_SCALE : dt;
    sw.t += bdt;
    sw.puffAcc = (sw.puffAcc || 0) + bdt;
    const erupt = clamp(sw.t / 0.55, 0, 1);
    if (sw.puffAcc >= 0.045 && erupt < 0.95) {
      sw.puffAcc = 0;
      const stemH = lerp(8, 145, 1 - Math.pow(1 - erupt, 3));
      const capR = lerp(20, 200, clamp((sw.t - 0.12) / 0.7, 0, 1));
      const px = sw.x + rand(-capR * 0.35, capR * 0.35);
      const py = sw.y - stemH * rand(0.55, 1.05) - capR * rand(0.05, 0.35);
      this.particles.push(new Particle(
        px, py, rand(-35, 35), rand(-70, -20),
        rand(0.45, 0.95), Math.random() < 0.55 ? "#5a7848" : "#3a5030",
        rand(12, 26), { kind: "smoke", grav: -12, drag: 0.93 }));
    }
  }
  this.meteorNukeSwamps = this.meteorNukeSwamps.filter(sw => sw.t < sw.life);
};

Game.prototype.drawMeteorNukeSwampCloud = function (sw) {
  const c = this.ctx;
  const p = clamp(sw.t / sw.life, 0, 1);
  const erupt = 1 - Math.pow(1 - Math.min(p * 2.4, 1), 3);
  const bloom = clamp((p - 0.06) / 0.62, 0, 1);
  const bloomE = 1 - Math.pow(1 - bloom, 2.2);
  const fade = 1 - clamp((p - 0.5) / 0.5, 0, 1);
  const alpha = fade * (0.92 + 0.08 * (1 - p));
  const wob = Math.sin(sw.t * 4.5 + sw.seed) * 0.07;
  const wob2 = Math.cos(sw.t * 3.1 + sw.seed * 1.7) * 0.05;

  c.save();
  c.translate(sw.x, sw.y);

  // Ground toxic pool
  const poolR = lerp(28, 110, erupt) * (1 + wob * 0.15);
  c.globalAlpha = alpha * 0.55;
  const pool = c.createRadialGradient(0, 0, 0, 0, 0, poolR);
  pool.addColorStop(0, rgba("#3a2818", 0.75));
  pool.addColorStop(0.45, rgba("#4a6038", 0.45));
  pool.addColorStop(1, rgba("#2a3820", 0));
  c.fillStyle = pool;
  c.beginPath();
  c.ellipse(0, 0, poolR, poolR * 0.72, 0, 0, TAU);
  c.fill();

  // Fiery eruption base
  const coreR = lerp(12, 48, erupt);
  c.globalAlpha = alpha * 0.7;
  const core = c.createRadialGradient(0, 0, 0, 0, 0, coreR);
  core.addColorStop(0, rgba("#ffe8c0", 0.85));
  core.addColorStop(0.35, rgba("#ff9040", 0.55));
  core.addColorStop(0.75, rgba("#6a5030", 0.25));
  core.addColorStop(1, rgba("#3a2818", 0));
  c.fillStyle = core;
  c.beginPath();
  c.ellipse(0, 0, coreR, coreR * 0.82, 0, 0, TAU);
  c.fill();

  // Mushroom stem — toxic column
  const stemH = lerp(10, 150, erupt);
  const stemW = lerp(16, 54, erupt) * (1 + wob * 0.12);
  c.globalAlpha = alpha * 0.62;
  const stem = c.createLinearGradient(0, 0, 0, -stemH);
  stem.addColorStop(0, rgba("#5a4028", 0.7));
  stem.addColorStop(0.4, rgba("#4a6838", 0.55));
  stem.addColorStop(0.85, rgba("#6a8050", 0.35));
  stem.addColorStop(1, rgba("#8a9868", 0.15));
  c.fillStyle = stem;
  c.beginPath();
  c.ellipse(wob * 8, -stemH * 0.48, stemW * 0.42, stemH * 0.52, wob * 0.3, 0, TAU);
  c.fill();

  // Mushroom cap — billowing swamp cloud
  const capR = lerp(24, 210, bloomE) * (1 + wob2 * 0.14);
  const capY = -stemH * 0.88 - capR * 0.22;
  c.globalAlpha = alpha * 0.58;
  const cap = c.createRadialGradient(wob * 12, capY, capR * 0.08, wob * 12, capY, capR);
  cap.addColorStop(0, rgba("#8a9868", 0.55));
  cap.addColorStop(0.35, rgba("#5a7848", 0.48));
  cap.addColorStop(0.68, rgba("#3a5030", 0.35));
  cap.addColorStop(1, rgba("#1a2414", 0));
  c.fillStyle = cap;
  c.beginPath();
  c.ellipse(wob * 14, capY, capR, capR * 0.58, wob2 * 0.25, 0, TAU);
  c.fill();

  // Cap highlight roll
  c.globalAlpha = alpha * 0.35;
  const roll = c.createRadialGradient(wob * 10, capY - capR * 0.12, 0, wob * 10, capY - capR * 0.12, capR * 0.55);
  roll.addColorStop(0, rgba("#b0c088", 0.45));
  roll.addColorStop(0.5, rgba("#708858", 0.18));
  roll.addColorStop(1, rgba("#405030", 0));
  c.fillStyle = roll;
  c.beginPath();
  c.ellipse(wob * 10, capY - capR * 0.08, capR * 0.72, capR * 0.38, wob2 * 0.2, 0, TAU);
  c.fill();

  // Secondary cap puff (mushroom lobe)
  if (bloomE > 0.15) {
    const lobeR = capR * lerp(0.45, 0.78, bloomE);
    const lobeY = capY + capR * 0.08;
    c.globalAlpha = alpha * 0.42;
    const lobe = c.createRadialGradient(-capR * 0.22, lobeY, 0, -capR * 0.22, lobeY, lobeR);
    lobe.addColorStop(0, rgba("#6a8050", 0.4));
    lobe.addColorStop(1, rgba("#2a3820", 0));
    c.fillStyle = lobe;
    c.beginPath();
    c.ellipse(-capR * 0.18 + wob2 * 20, lobeY, lobeR, lobeR * 0.5, -0.2, 0, TAU);
    c.fill();
  }

  c.restore();
  c.globalAlpha = 1;
};

Game.prototype.drawMeteorNukeSwamps = function () {
  if (!this.meteorNukeSwamps?.length) return;
  for (const sw of this.meteorNukeSwamps) {
    this.drawMeteorNukeSwampCloud(sw);
  }
};

Game.prototype.meteorNukeCloudRadius = function (cloud) {
  const p = clamp(cloud.t / cloud.life, 0, 1);
  const eased = 1 - (1 - p) * (1 - p);
  return Math.max(28, cloud.maxR * eased);
};

Game.prototype.meteorNukeSectorBlocked = function (cloud, sector) {
  const reach = cloud.reach[sector];
  return reach < cloud.maxR - NUKE_REACH_BLOCKED_SLACK && reach >= NUKE_MIN_BLOCK_REACH;
};

Game.prototype.meteorNukeSectorFront = function (cloud, sector, rNow) {
  if (!this.meteorNukeSectorBlocked(cloud, sector)) return rNow;
  return Math.min(rNow, cloud.reach[sector]);
};

Game.prototype.infernoNovaFuseNear = function (x, y) {
  for (const f of this.meteorNukeFuses || []) {
    if (Math.hypot(f.x - x, f.y - y) < 80) return f;
  }
  return null;
};

/** Unified lethal boss channel telegraph (Inferno Nova + Plague Burst). */
Game.prototype.lethalChannelProgress = function (boss) {
  if (!boss?.boss || !boss.alive) return null;

  let spell, windup, kind;
  if (boss.boss === "angel") {
    spell = "boss_judgment_nova";
    windup = Game.INFERNO_NOVA_WINDUP;
    kind = "inferno";
  } else if (boss.boss === "groshkar") {
    spell = "boss_plague_burst";
    windup = Game.PLAGUE_BURST_WINDUP;
    kind = "plague";
  } else {
    return null;
  }

  const fuseDur = Game.JUDGMENT_FUSE_DUR;
  const total = windup + fuseDur;

  if (boss.channelSpell === spell && boss.channelProg > 0) {
    const cp = clamp(boss.channelProg, 0, 1);
    return {
      active: true,
      prog: (cp * windup) / total,
      channelProg: cp,
      phase: "channel",
      urgent: false,
      kind,
    };
  }
  const fuse = this.infernoNovaFuseNear(boss.x, boss.y);
  if (fuse) {
    const fuseProg = clamp(fuse.t / fuse.life, 0, 1);
    return {
      active: true,
      prog: (windup + fuseProg * fuseDur) / total,
      channelProg: 1,
      phase: "fuse",
      urgent: fuseProg > 0.72,
      kind,
    };
  }
  return null;
};

Game.prototype.infernoNovaProgress = Game.prototype.lethalChannelProgress;

/** Active boss fight id — PvE round boss or sandbox boss-fight toggle. */
Game.prototype.activeBossId = function () {
  if (this.matchMode === "pve") return this.pveBossId || "";
  if (this.matchMode === "sandbox") {
    if (this.sandboxBossFight) return this.sandboxBossFight;
    const boss = this.players.find(pl => pl.boss && pl.alive);
    return boss?.boss || "";
  }
  return "";
};

/** True when boss channel HUD (lethal / shatter / gale) should draw. */
Game.prototype.showBossChannelHud = function () {
  if (this.state !== "playing") return false;
  if (this.matchMode === "pve") return true;
  return this.matchMode === "sandbox" && !!this.activeBossId();
};

/** Vorathal Shatter channel telegraph (keep moving or die). */
Game.prototype.shatterChannelProgress = function () {
  if (!this.showBossChannelHud()) return null;
  const boss = this.players.find(pl => pl.boss === "vorathal" && pl.alive);
  if (!boss || boss.channelSpell !== "boss_shatter") return null;
  const cp = clamp(boss.channelProg, 0, 1);
  return { active: true, prog: Math.max(cp, 0.04), channelProg: cp, urgent: cp > 0.65, kind: "shatter" };
};

/** Vorathal Howling Gale windup or active push cone. */
Game.prototype.howlingGaleProgress = function () {
  if (!this.showBossChannelHud()) return null;
  const boss = this.players.find(pl => pl.boss === "vorathal" && pl.alive);
  if (!boss) return null;
  if (boss.channelSpell === "boss_howling_gale") {
    const cp = clamp(boss.channelProg, 0, 1);
    return { active: true, prog: Math.max(cp, 0.04), phase: "windup", urgent: cp > 0.65 };
  }
  if ((this.windFields || []).some(wf => wf.spellId === "boss_howling_gale" && wf.life > 0)) {
    return { active: true, prog: 1, phase: "active", urgent: true };
  }
  return null;
};

/** Ix'tholoth Void Bloom windup or active orb spiral. */
Game.prototype.voidBloomProgress = function () {
  if (!this.showBossChannelHud()) return null;
  const boss = this.players.find(pl => pl.boss === "ixtholoth" && pl.alive);
  if (!boss) return null;
  if (boss.channelSpell === "boss_void_bloom") {
    const cp = clamp(boss.channelProg, 0, 1);
    return { active: true, prog: Math.max(cp, 0.04), phase: "windup", urgent: cp > 0.65 };
  }
  if ((this.projectiles || []).some(pj => pj.spellId === "boss_void_bloom")) {
    return { active: true, prog: 1, phase: "active", urgent: true };
  }
  return null;
};

/** True after the 25% HP rift-collapse event. */
Game.prototype.ixtholothCollapseActive = function () {
  return this.activeBossId?.() === "ixtholoth" && !!this.ixtholothRiftCollapsed;
};

/** Twin Sisters Essence Bloom / Meridian — countdown to next beat or snap. */
Game.prototype.essenceCastProgress = function () {
  if (this.activeBossId() !== "sisters" || this.state !== "playing") return null;
  const ec = this.essenceCast;
  if (!ec?.k) return null;

  const moveSec = 1.0;
  const batchTelegraph = 0.7;
  const rippleGap = 0.42;
  const windupTotal = 3.0;
  const meridianRotate = 3.5;

  if (ec.k === "bloom") {
    if (!ec.n || !ec.b) return null;
    let untilPulse = ec.t || 0;
    let span = rippleGap;
    if (ec.ph === "windup") {
      untilPulse = ec.t + moveSec + batchTelegraph;
      span = windupTotal + moveSec + batchTelegraph;
    } else if (ec.ph === "move") {
      untilPulse = ec.t + batchTelegraph;
      span = moveSec + batchTelegraph;
    } else if (ec.ph === "telegraph") {
      untilPulse = ec.t;
      span = batchTelegraph;
    } else if (ec.ph === "ripple") {
      untilPulse = ec.t;
      span = rippleGap;
    }
    const prog = span > 0 ? clamp(1 - untilPulse / span, 0, 1) : 1;
    const urgent = ec.ph === "telegraph" && untilPulse > 0 && untilPulse < 0.8;
    return {
      active: true,
      kind: "bloom",
      prog,
      urgent,
      needMark: ec.n,
      pulseMark: ec.pl || (ec.b === 2 ? 2 : 1),
      beat: ec.b,
      ripple: ec.rp || 1,
      phase: ec.ph || "ripple",
      untilPulse,
    };
  }

  if (ec.k === "meridian") {
    let untilPulse = ec.t || 0;
    let span = meridianRotate;
    if (ec.ph === "windup") {
      untilPulse = ec.t + moveSec + meridianRotate;
      span = windupTotal + moveSec + meridianRotate;
    } else if (ec.ph === "move") {
      untilPulse = ec.t + meridianRotate;
      span = moveSec + meridianRotate;
    } else if (ec.ph === "telegraph") {
      untilPulse = ec.t;
      span = meridianRotate;
    }
    const prog = span > 0 ? clamp(1 - untilPulse / span, 0, 1) : 1;
    const urgent = untilPulse > 0 && untilPulse < 0.8;
    return {
      active: true,
      kind: "meridian",
      prog,
      urgent,
      phase: ec.ph || "telegraph",
      untilPulse,
      angle: ec.an || 0,
    };
  }

  return null;
};

Game.prototype.meridianPlayerReady = function (p, angle) {
  if (!p?.alive || !p.essenceMark) return false;
  const side = this.meridianSide(p.x, p.y, 0, -80, angle);
  return (p.essenceMark === 2 && side === 1) || (p.essenceMark === 1 && side === 2);
};

Game.prototype.meridianSide = function (x, y, cx, cy, angle) {
  const dx = x - cx, dy = y - cy;
  const dot = dx * Math.cos(angle) + dy * Math.sin(angle);
  return dot >= 0 ? 1 : 2;
};

Game.prototype.spawnJudgmentNovaCharge = function (x, y, prog) {
  const p = clamp(prog, 0, 1);
  this.spawnShockwave(x, y, 70 + p * 160, "#ff6030", { life: 0.42, width: 5 + p * 7 });
  this.spawnShockwave(x, y, 110 + p * 220, "#ff8844", { life: 0.5, width: 3 });
  this.spawnParticles(x, y, 10 + Math.floor(p * 16), "#ffb060", 120 + p * 140, 0.45, { kind: "glow" });
  this.spawnParticles(x, y, 6 + Math.floor(p * 10), "#ff5020", 90 + p * 100, 0.35, { kind: "spark" });
};

Game.prototype.spawnMeteorNukeFuse = function (x, y, life) {
  if (!this.meteorNukeFuses) this.meteorNukeFuses = [];
  this.meteorNukeFuses = this.meteorNukeFuses.filter(f => Math.hypot(f.x - x, f.y - y) > 36);
  this.meteorNukeFuses.push({
    x, y, t: 0, life: life || Game.METEOR_NUKE_FUSE_DUR, flash: 0, tick: 0,
  });
};

Game.prototype.pulseMeteorNukeFuse = function (x, y, secsLeft) {
  for (const f of this.meteorNukeFuses || []) {
    if (Math.hypot(f.x - x, f.y - y) < 52) {
      f.flash = 1;
      f.tick = secsLeft || 0;
    }
  }
};

Game.prototype.clearMeteorNukeFuse = function (x, y) {
  if (!this.meteorNukeFuses?.length) return;
  this.meteorNukeFuses = this.meteorNukeFuses.filter(
    f => Math.hypot(f.x - x, f.y - y) > 52);
};

Game.prototype.updateMeteorNukeFuses = function (dt) {
  if (!this.meteorNukeFuses?.length) return;
  for (const f of this.meteorNukeFuses) {
    f.t += dt;
    f.flash = Math.max(0, f.flash - dt * 2.8);
  }
  this.meteorNukeFuses = this.meteorNukeFuses.filter(f => f.t < f.life);
};

Game.prototype.drawMeteorNukeFuses = function () {
  if (!this.meteorNukeFuses?.length) return;
  const c = this.ctx;
  for (const f of this.meteorNukeFuses) {
    const prog = clamp(f.t / f.life, 0, 1);
    const urgency = prog * prog;
    const pump = 0.58 + 0.42 * Math.sin(f.t * TAU);
    const flash = f.flash;
    const baseR = 34 + urgency * 38;
    const r1 = baseR * (pump + flash * 0.42);
    const r2 = baseR * 0.62 * (pump * 0.88 + flash * 0.35);
    const alpha = 0.28 + urgency * 0.38 + flash * 0.28;

    c.save();
    c.translate(f.x, f.y);
    c.globalAlpha = alpha * 0.45;
    const fill = c.createRadialGradient(0, 0, 0, 0, 0, r1);
    fill.addColorStop(0, rgba("#ff6040", 0.55 + flash * 0.3));
    fill.addColorStop(0.45, rgba("#ff9050", 0.28));
    fill.addColorStop(1, rgba("#ff5020", 0));
    c.fillStyle = fill;
    c.beginPath();
    c.arc(0, 0, r1, 0, TAU);
    c.fill();

    c.globalAlpha = alpha;
    c.strokeStyle = rgba(flash > 0.2 ? "#ffe0a0" : "#ff6840", 0.75 + flash * 0.2);
    c.lineWidth = 2.5 + flash * 2 + urgency * 1.5;
    c.setLineDash([10, 8]);
    c.lineDashOffset = -f.t * 48;
    c.beginPath();
    c.arc(0, 0, r1, 0, TAU);
    c.stroke();

    c.globalAlpha = alpha * 0.55;
    c.strokeStyle = rgba("#ff9060", 0.5);
    c.lineWidth = 1.5;
    c.setLineDash([6, 10]);
    c.lineDashOffset = f.t * 36;
    c.beginPath();
    c.arc(0, 0, r2, 0, TAU);
    c.stroke();
    c.setLineDash([]);
    c.restore();
  }
  c.globalAlpha = 1;
};

Game.prototype.spawnMeteorNukeVapor = function (x, y, ang, scale = 1) {
  const ca = Math.cos(ang), sa = Math.sin(ang);
  for (let i = 0; i < 7; i++) {
    const spread = rand(-0.55, 0.55);
    const dir = ang + spread;
    const sp = rand(40, 130) * scale;
    this.particles.push(new Particle(
      x + ca * rand(-8, 8), y + sa * rand(-8, 8),
      Math.cos(dir) * sp + ca * rand(10, 40), Math.sin(dir) * sp + sa * rand(10, 40),
      rand(0.28, 0.62), Math.random() < 0.45 ? "#e8dcd0" : "#a89080",
      rand(8, 18) * scale, { kind: "smoke", grav: -22, drag: 0.9 }));
  }
  for (let i = 0; i < 4; i++) {
    this.particles.push(new Particle(
      x, y, ca * rand(-30, 30) + rand(-25, 25), sa * rand(-30, 30) + rand(-25, 25),
      rand(0.18, 0.38), "#fff0e0", rand(4, 9), { kind: "glow", grav: -35, drag: 0.88 }));
  }
};

Game.prototype.updateMeteorNukeClouds = function (dt) {
  if (!this.meteorNukeClouds?.length) return;
  for (const cloud of this.meteorNukeClouds) {
    const bdt = cloud.slowHalf ? dt * Game.BULLET_TIME_FX_SCALE : dt;
    cloud.t += bdt;
    const rNow = this.meteorNukeCloudRadius(cloud);
    const prevR = cloud.prevR || 0;
    this.kickVoidDebrisFromNukeCloud(cloud, prevR, rNow);
    const n = cloud.reach.length;

    for (let i = 0; i < n; i++) {
      if (!this.meteorNukeSectorBlocked(cloud, i)) continue;
      const cap = cloud.reach[i];
      const front = this.meteorNukeSectorFront(cloud, i, rNow);
      if (front < cap - 8) continue;
      if (!cloud.blocked[i]) {
        cloud.blocked[i] = 1;
        const a = (i / n) * TAU;
        this.spawnMeteorNukeVapor(
          cloud.x + Math.cos(a) * cap, cloud.y + Math.sin(a) * cap, a, 1.1);
      }
      cloud.blockedAge[i] = Math.min(1, cloud.blockedAge[i] + bdt * 2.4);
    }

    cloud.partAcc = (cloud.partAcc || 0) + bdt;
    if (cloud.partAcc >= 0.018) {
      cloud.partAcc = 0;
      for (let k = 0; k < 5; k++) {
        const i = Math.floor(Math.random() * n);
        if (this.meteorNukeSectorBlocked(cloud, i)) continue;
        const a = (i / n) * TAU;
        const front = rNow;
        const px = cloud.x + Math.cos(a) * front * rand(0.96, 1.0);
        const py = cloud.y + Math.sin(a) * front * rand(0.96, 1.0);
        this.particles.push(new Particle(
          px, py, Math.cos(a) * rand(60, 140), Math.sin(a) * rand(60, 140),
          rand(0.22, 0.48), Math.random() < 0.5 ? "#ffd0a8" : "#ffb878",
          rand(5, 11), { kind: "glow", grav: -18, drag: 0.9 }));
      }
    }
    cloud.prevR = rNow;
  }
  this.meteorNukeClouds = this.meteorNukeClouds.filter(c => c.t < c.life);
};

Game.prototype.drawMeteorNukeCloudRing = function (cloud) {
  const c = this.ctx;
  const n = cloud.reach.length;
  const sw = TAU / n;
  const rNow = this.meteorNukeCloudRadius(cloud);
  const ringW = Game.METEOR_NUKE_RING_WIDTH;
  const rInner = Math.max(0, rNow - ringW);
  const lifeFade = 1 - clamp(cloud.t / cloud.life, 0, 1) * 0.2;

  const open = new Array(n);
  for (let i = 0; i < n; i++) open[i] = !this.meteorNukeSectorBlocked(cloud, i);

  c.save();
  c.translate(cloud.x, cloud.y);

  // Open directions — smooth annulus arcs (full shockwave ring).
  circularRuns(open, (start, count, isOpen) => {
    if (!isOpen) return;
    const a0 = start * sw - sw * 0.5;
    const a1 = (start + count) * sw - sw * 0.5;
    fillAnnulusArc(c, a0, a1, rInner, rNow);
    const grd = c.createRadialGradient(0, 0, rInner, 0, 0, rNow);
    grd.addColorStop(0, rgba("#6a4830", 0));
    grd.addColorStop(0.35, rgba("#c87840", lifeFade * 0.35));
    grd.addColorStop(0.72, rgba("#ffb868", lifeFade * 0.62));
    grd.addColorStop(1, rgba("#fff0d8", lifeFade * 0.88));
    c.fillStyle = grd;
    c.fill();
    c.strokeStyle = rgba("#fff4e8", lifeFade * 0.85);
    c.lineWidth = 7;
    c.lineCap = "round";
    c.beginPath();
    c.arc(0, 0, rNow, a0, a1);
    c.stroke();
    c.strokeStyle = rgba("#ff7030", lifeFade * 0.45);
    c.lineWidth = 3;
    c.stroke();
  });

  // Blocked directions — capped wedge at the obstacle face (ring stops at cover).
  for (let i = 0; i < n; i++) {
    if (open[i]) continue;
    const cap = this.meteorNukeSectorFront(cloud, i, rNow);
    if (cap < NUKE_MIN_BLOCK_REACH) continue;
    const vapor = cloud.blockedAge[i];
    const a0 = i * sw - sw * 0.5;
    const a1 = i * sw + sw * 0.5;
    const rOut = cap;
    const rIn = Math.max(0, cap - ringW);
    if (rOut > rIn + 2) {
      fillAnnulusArc(c, a0, a1, rIn, rOut);
      const segAlpha = lifeFade * (cloud.blocked[i] ? Math.max(0.2, 0.75 - vapor * 0.55) : 0.6);
      const capGrd = c.createRadialGradient(0, 0, rIn, 0, 0, rOut);
      capGrd.addColorStop(0, rgba("#5a4038", 0));
      capGrd.addColorStop(0.5, rgba("#a89078", segAlpha * 0.45));
      capGrd.addColorStop(1, rgba("#f0e4d8", segAlpha * 0.8));
      c.fillStyle = capGrd;
      c.fill();
      c.strokeStyle = rgba("#e8dcd0", segAlpha * 0.85);
      c.lineWidth = 5;
      c.beginPath();
      c.arc(0, 0, rOut, a0, a1);
      c.stroke();
    }
    if (cloud.blocked[i] && vapor > 0) {
      const px = Math.cos(i * sw) * cap;
      const py = Math.sin(i * sw) * cap;
      const puffR = 16 + vapor * 24;
      c.globalAlpha = lifeFade * (1 - vapor) * 0.55;
      const puff = c.createRadialGradient(px, py, 0, px, py, puffR);
      puff.addColorStop(0, rgba("#fff8f0", 0.65));
      puff.addColorStop(0.4, rgba("#c8b8a8", 0.25));
      puff.addColorStop(1, rgba("#908070", 0));
      c.fillStyle = puff;
      c.beginPath();
      c.arc(px, py, puffR, 0, TAU);
      c.fill();
      c.globalAlpha = 1;
    }
  }

  c.restore();
  c.globalAlpha = 1;
};

Game.prototype.drawMeteorNukeCloudUmbra = function (cloud) {
  const c = this.ctx;
  const n = cloud.reach.length;
  const sw = TAU / n;
  const rNow = this.meteorNukeCloudRadius(cloud);
  const ringW = Game.METEOR_NUKE_RING_WIDTH;
  const lifeFade = 1 - clamp(cloud.t / cloud.life, 0, 1) * 0.2;

  const umbra = new Array(n);
  for (let i = 0; i < n; i++) {
    umbra[i] = this.meteorNukeSectorInUmbra(cloud, i) && rNow >= this.meteorNukeUmbraCap(cloud, i) - 2;
  }

  c.save();
  c.translate(cloud.x, cloud.y);

  circularRuns(umbra, (start, count, inUmbra) => {
    if (!inUmbra) return;
    let capMin = cloud.maxR;
    for (let j = 0; j < count; j++) {
      capMin = Math.min(capMin, this.meteorNukeUmbraCap(cloud, (start + j) % n));
    }
    const a0 = start * sw - sw * 0.5;
    const a1 = (start + count) * sw - sw * 0.5;
    const rOut = cloud.maxR + ringW;
    fillAnnulusArc(c, a0, a1, capMin, rOut);
    const umbraGrd = c.createRadialGradient(0, 0, capMin, 0, 0, rOut);
    umbraGrd.addColorStop(0, rgba("#060404", lifeFade * 0.78));
    umbraGrd.addColorStop(0.18, rgba("#0c0806", lifeFade * 0.62));
    umbraGrd.addColorStop(0.5, rgba("#120c0a", lifeFade * 0.4));
    umbraGrd.addColorStop(0.82, rgba("#181008", lifeFade * 0.18));
    umbraGrd.addColorStop(1, rgba("#181008", 0));
    c.fillStyle = umbraGrd;
    c.fill();
    const penR = Math.max(capMin - ringW * 0.35, 0);
    if (capMin > penR + 4) {
      fillAnnulusArc(c, a0, a1, penR, capMin + ringW * 0.15);
      const pen = c.createRadialGradient(0, 0, penR, 0, 0, capMin + ringW * 0.4);
      pen.addColorStop(0, rgba("#080604", 0));
      pen.addColorStop(0.55, rgba("#0a0705", lifeFade * 0.35));
      pen.addColorStop(1, rgba("#100a08", lifeFade * 0.55));
      c.fillStyle = pen;
      c.fill();
    }
  });

  c.restore();
  c.globalAlpha = 1;
};

Game.prototype.drawMeteorNukeUmbra = function () {
  if (!this.meteorNukeClouds?.length) return;
  for (const cloud of this.meteorNukeClouds) {
    this.drawMeteorNukeCloudUmbra(cloud);
  }
};

Game.prototype.drawMeteorNukeClouds = function () {
  if (!this.meteorNukeClouds?.length) return;
  for (const cloud of this.meteorNukeClouds) {
    this.drawMeteorNukeCloudRing(cloud);
  }
};

Game.prototype.spawnArcaneDetonationOrb = function (x, y, delay) {
  if (!this.arcaneDetonationOrbs) this.arcaneDetonationOrbs = [];
  const plat = this.platformRadius || this.maxRadius || 950;
  const blastR = 100 * (plat / (this.maxRadius || 950));
  this.arcaneDetonationOrbs.push({
    x, y, t: 0, life: Math.max(0.5, delay || 1.2), flash: 0, tick: 0, blastR,
  });
};

Game.prototype.updateArcaneDetonationOrbs = function (dt) {
  if (!this.arcaneDetonationOrbs?.length) return;
  for (const f of this.arcaneDetonationOrbs) {
    f.t += dt;
    f.flash = Math.max(0, f.flash - dt * 2.8);
  }
  this.arcaneDetonationOrbs = this.arcaneDetonationOrbs.filter(f => f.t < f.life);
};

Game.prototype.drawArcaneDetonationOrbs = function () {
  if (!this.arcaneDetonationOrbs?.length) return;
  const c = this.ctx;
  for (const f of this.arcaneDetonationOrbs) {
    const prog = clamp(f.t / f.life, 0, 1);
    const urgency = prog * prog;
    const secsLeft = Math.max(0, f.life - f.t);
    const pump = 0.58 + 0.42 * Math.sin(f.t * TAU * 1.4);
    const flash = f.flash;
    const baseR = 38 + urgency * 32;
    const r1 = baseR * (pump + flash * 0.42);
    const r2 = baseR * 0.62 * (pump * 0.88 + flash * 0.35);
    const blastR = f.blastR || 100 * ((this.platformRadius || this.maxRadius || 950) / (this.maxRadius || 950));
    const alpha = 0.42 + urgency * 0.48 + flash * 0.28;

    c.save();
    c.translate(f.x, f.y);

    // lethal blast zone — filled disc + bright rim (reads like meteor telegraph)
    c.globalAlpha = 0.14 + urgency * 0.22 + flash * 0.12;
    c.fillStyle = rgba("#9040d0", 0.55 + urgency * 0.35);
    c.beginPath(); c.arc(0, 0, blastR, 0, TAU); c.fill();

    c.save();
    c.globalCompositeOperation = "lighter";
    c.globalAlpha = 0.28 + urgency * 0.42 + flash * 0.25;
    c.strokeStyle = rgba(flash > 0.2 ? "#f0d0ff" : "#c080ff", 0.65 + urgency * 0.3);
    c.lineWidth = 3 + urgency * 2.5 + flash * 1.5;
    c.setLineDash([12, 8]);
    c.lineDashOffset = -f.t * 32;
    c.beginPath(); c.arc(0, 0, blastR, 0, TAU); c.stroke();
    c.setLineDash([]);
    c.globalAlpha = 0.55 + urgency * 0.35;
    c.strokeStyle = rgba("#e0a0ff", 0.75 + flash * 0.2);
    c.lineWidth = 2;
    c.beginPath(); c.arc(0, 0, blastR * (1 - prog * 0.35) + 12, 0, TAU); c.stroke();
    // rim crosshair ticks
    c.beginPath();
    for (let i = 0; i < 4; i++) {
      const a = i * TAU / 4 + Math.PI / 4;
      c.moveTo(Math.cos(a) * (blastR - 14), Math.sin(a) * (blastR - 14));
      c.lineTo(Math.cos(a) * blastR, Math.sin(a) * blastR);
    }
    c.stroke();
    c.restore();

    // charging orb core
    c.globalAlpha = alpha * 0.55;
    const fill = c.createRadialGradient(0, 0, 0, 0, 0, r1);
    fill.addColorStop(0, rgba("#f0d0ff", 0.75 + flash * 0.2));
    fill.addColorStop(0.4, rgba("#b060e8", 0.45 + urgency * 0.2));
    fill.addColorStop(1, rgba("#6020a0", 0));
    c.fillStyle = fill;
    c.beginPath(); c.arc(0, 0, r1, 0, TAU); c.fill();

    c.globalAlpha = alpha;
    c.strokeStyle = rgba(flash > 0.2 ? "#fff0ff" : "#d080ff", 0.85 + flash * 0.15);
    c.lineWidth = 2.5 + flash * 2 + urgency * 1.5;
    c.setLineDash([10, 7]);
    c.lineDashOffset = -f.t * 48;
    c.beginPath(); c.arc(0, 0, r1, 0, TAU); c.stroke();

    c.globalAlpha = alpha * 0.65;
    c.strokeStyle = rgba("#c080ff", 0.6);
    c.lineWidth = 1.5;
    c.setLineDash([6, 9]);
    c.lineDashOffset = f.t * 36;
    c.beginPath(); c.arc(0, 0, r2, 0, TAU); c.stroke();
    c.setLineDash([]);

    // bright nucleus + fuse countdown on final seconds
    c.globalAlpha = 0.85 + flash * 0.15;
    c.fillStyle = rgba("#fff0ff", 0.9);
    c.beginPath(); c.arc(0, 0, 6 + urgency * 4 + flash * 3, 0, TAU); c.fill();
    if (secsLeft <= 3.5) {
      c.font = uiFont(800, "md");
      c.textAlign = "center";
      c.textBaseline = "middle";
      c.fillStyle = rgba("#fff0ff", 0.95);
      c.fillText(String(Math.ceil(secsLeft)), 0, -r1 - 14);
    }

    c.restore();
  }
};
